<template>
  <span class="ee-help-wrap">
    <b-button
      size="sm"
      variant="outline-secondary"
      title="How to work an event"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Working an event"
      size="lg"
      ok-only
      scrollable
    >
      <div class="ee-help-body">
        <h4>What this page is</h4>
        <p>
          The full record of one event, and the place it gets worked: its status, who owns
          it, what was done about it, the photos, and the conversation. The back arrow returns
          you to the Event Log or the Event Board, whichever you came from.
        </p>

        <hr/>

        <h4>Status</h4>
        <ul>
          <li>
            <b>New</b> means filed and not yet picked up. <b>Start</b> moves it to
            <b>In progress</b>. <b>Awaiting follow-up</b> is for waiting on a callback, a
            part, or someone else. <b>Resolve</b> closes it out.
          </li>
          <li>
            The <b>Move to</b> buttons only offer the moves the workflow allows, so nothing
            you pick will be refused after the fact. In progress and Awaiting follow-up can go
            back and forth; nothing returns to New.
          </li>
          <li>
            Reopening a resolved event (back to In progress) is limited to Tech Support and
            fleet admins, so a stray click can't reopen closed work.
          </li>
          <li>
            When the status changes, the person who filed the event gets a short update. The
            other default recipients are not re-notified.
          </li>
        </ul>

        <hr/>

        <h4>Editing the details</h4>
        <ul>
          <li>
            <b>Edit</b> in the header opens every editable field at once; <b>Save</b> or
            <b>Discard</b> closes them. The <b>+ Add</b> link on an empty Action items or
            Follow-up card opens edit mode straight at that field.
          </li>
          <li>
            <b>What happened</b> is the original submission and stays read-only. When an issue
            or kudos becomes a note, its old category and answers are added to the end.
            Everything else can change as you learn more: the category, driver, vehicle,
            charter reference, customer, assignee, severity, whether the customer was delayed
            and for how long, the action items, and the follow-up and resolution.
          </li>
          <li>
            <b>Assigned</b> is a picker of the people on your account, so an owner is always
            a real user who can be notified and filtered on. <b>Assign to me</b> takes it in
            one click. Whoever you assign gets a card in their Notification Center, and an email
            if their login has an email address and Event Response Manager emails are on in
            their profile. Assigning an event to yourself notifies nobody.
          </li>
          <li>
            The severity chip sits beside the status. Normal is the quiet default and shows no
            chip; High and Low do. Change it under Edit.
          </li>
          <li>
            Tech Support and fleet admins also see <b>Delete</b> under Edit, for an entry filed in
            error. It removes the event for good, with its comments, activity, and photos, and
            the number is never reused. To archive a whole category instead, turn it off under Settings.
          </li>
          <li>
            Tech Support and fleet admins can change an issue or kudos that should have been a
            note: pick <b>Note</b> under <b>Type</b>, then <b>Save</b>. It keeps its number,
            status, comments, activity and photos. Its category and answers are copied to the end
            of <b>What happened</b>, and from then on it counts as a note in analytics. Nobody is
            notified, and this can't be undone. An event linked to a coaching or recognition
            ticket keeps its type.
          </li>
        </ul>

        <hr/>

        <h4>Driver and vehicle</h4>
        <ul>
          <li>
            A driver name or vehicle typed on the entry can be matched to your roster with
            <b>Link</b>. A linked event shows a check mark and the roster's name or unit
            number, so the record points at a real driver or unit rather than a spelling of
            one.
          </li>
          <li>
            When a linked driver has a scorecard, their combined score appears as a chip;
            click it to open the scorecard.
          </li>
          <li>
            In edit mode the Driver and Vehicle fields suggest from the roster as you type.
            A value that isn't on the roster is still accepted.
          </li>
        </ul>

        <hr/>

        <h4>Photos and submission details</h4>
        <ul>
          <li>
            Photos appear in a gallery; click one to open it full size. <b>+ Add photos</b>
            attaches more, up to 10 per event (JPEG, PNG, GIF, or WebP). The <b>&times;</b>
            on a tile removes it, with a confirm step, in case it was attached by mistake.
          </li>
          <li>
            <b>Submission details</b> lists the extra questions the reporter answered for
            this category. It is part of the original submission, so it is read-only.
          </li>
        </ul>

        <hr/>

        <h4>Activity</h4>
        <ul>
          <li>
            <b>Post comment</b> adds a finding to the record and notifies the category's
            recipients (the default recipients unless the category or notes has its own).
          </li>
          <li>
            The stream mixes your comments with an automatic record of what changed: status
            moves, field edits, and photos added or removed. Edits to short fields such as the
            assignee, severity, or driver show the old and new values. Edits to free-text
            fields such as the action items record that they changed, but not the text.
          </li>
          <li>
            You can reply directly to any Event Response Manager notification email, and your
            reply lands here as a comment with a small <b>via email</b> chip. Photos attached
            to the email are saved to the event. Reply from the address that received the
            notification: a reply from an address the system doesn't recognize is discarded
            without a bounce.
          </li>
        </ul>

        <hr/>

        <h4>Coaching and classification</h4>
        <p>
          Tech Support and fleet admins see an extra card for <b>Preventability</b> and a
          <b>Coaching note</b>. On fleets that run the Coaching Portal, <b>Assign
          coaching</b> (or <b>Recognize driver</b> on a kudos) raises a ticket from this
          event, and the ticket is linked back here so the two stay in step.
        </p>

        <hr/>

        <h4>Also on this page</h4>
        <ul>
          <li>
            On a breakdown or accident, fleets with Bus SOS turned on see an <b>Open Bus
            SOS</b> shortcut with the vehicle and location filled in.
          </li>
          <li>
            <b>Print</b> produces a clean single-column copy of the full record, ready for a
            claim file or a safety meeting.
          </li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
// how-to modal for the ERM event detail view, following the erm_board_help /
// driver_manager_*_help house pattern (info-icon button + ok-only scrollable
// modal). the detail page is the busiest erm surface (workflow, editing,
// linking, photos, activity, coaching handoff), so this is where each of
// those gets a paragraph.
export default {
  name: 'erm_event_help',
  data() {
    return { show_help: false }
  },
}
</script>

<style scoped>
.ee-help-body h4 { font-size: 1.05rem; margin-top: 4px; }
.ee-help-body p, .ee-help-body li { font-size: 0.95rem; }
.ee-help-body li { margin-bottom: 6px; }
</style>
